/** The school's public homepage (module Homepage, GET /public/homepage and /school/homepage). */

export const BLOCK_TYPES = [
  'text',
  'highlights',
  'programmes',
  'admissions',
  'list',
  'announcements',
  'faq',
  'gallery',
  'contact',
] as const
export type BlockType = (typeof BLOCK_TYPES)[number]

/** Filled from the school's data; at most one of each per page. */
export const AUTO_BLOCK_TYPES: readonly BlockType[] = ['programmes', 'admissions', 'announcements', 'contact']

export const HIGHLIGHT_ICONS = [
  'star',
  'award',
  'book',
  'users',
  'heart',
  'shield',
  'globe',
  'flask',
  'music',
  'trophy',
  'bus',
  'meal',
  'clock',
  'graduation',
  'sparkles',
  'laptop',
] as const
export type HighlightIcon = (typeof HIGHLIGHT_ICONS)[number]

export interface Highlight {
  title: string
  text: string | null
  icon: HighlightIcon
}

export interface FaqItem {
  question: string
  answer: string
}

export interface OpeningHour {
  dayOfWeek: number
  isOpen: boolean
  openTime: string | null
  closeTime: string | null
}

export interface HomepageSchool {
  name: string
  shortName: string | null
  motto: string | null
  logoUrl: string | null
}

export interface HomepageContact {
  address: string | null
  phone: string | null
  email: string | null
  website: string | null
  openingHours: OpeningHour[]
}

export interface HomepageClass {
  name: string
  level: number
  series: string | null
  cycle: string | null
}

export interface HomepageNews {
  id: number
  title: string
  body: string
  truncated: boolean
  category: string
  publishedAt: string | null
}

/** A block as visitors get it: only the fields of its type, automatic content filled in. */
export type PublicBlock = { id: string; title: string | null } & (
  | { type: 'text'; body: string | null }
  | { type: 'list'; body: string | null; items: string[] }
  | { type: 'highlights'; highlights: Highlight[] }
  | { type: 'faq'; faq: FaqItem[] }
  | { type: 'gallery'; images: { url: string; caption: string | null }[] }
  | { type: 'programmes'; body: string | null; classes: HomepageClass[] }
  | { type: 'admissions'; body: string | null }
  | { type: 'announcements'; items: HomepageNews[] }
  | { type: 'contact'; body: string | null; mapUrl: string | null }
)

export interface PublicHomepage {
  enabled: true
  school: HomepageSchool
  heroTitle: string | null
  heroSubtitle: string | null
  coverUrl: string | null
  accentColor: string | null
  contact: HomepageContact
  blocks: PublicBlock[]
  updatedAt: string | null
}

export type PublicHomepageResponse = PublicHomepage | { enabled: false }

export interface EditorImage {
  fileId: number
  url: string | null
  caption: string | null
}

/** A block as the editor keeps it: every field, whatever the type. */
export interface EditorBlock {
  id: string
  type: BlockType
  title: string | null
  visible: boolean
  body: string | null
  items: string[]
  highlights: Highlight[]
  faq: FaqItem[]
  images: EditorImage[]
  limit: number | null
  mapUrl: string | null
}

export interface HomepageEditorData {
  enabled: boolean
  heroTitle: string | null
  heroSubtitle: string | null
  cover: { fileId: number; url: string | null } | null
  accentColor: string | null
  blocks: EditorBlock[]
  updatedAt: string | null
  /** The page's address; null in single-address mode (no school subdomain). */
  publicUrl: string | null
  context: {
    school: HomepageSchool
    contact: HomepageContact
    classes: HomepageClass[]
    announcements: HomepageNews[]
  }
}

export interface HomepagePayload {
  enabled: boolean
  heroTitle: string | null
  heroSubtitle: string | null
  coverFileId: number | null
  accentColor: string | null
  blocks: (Omit<EditorBlock, 'images'> & { images: { fileId: number; caption: string | null }[] })[]
}

/** GET /public/admissions (admissions module). Absent (404) when the school takes no online applications. */
export interface PublicAdmissions {
  open: boolean
  academicYear: { id: number; name: string } | null
  opensOn: string | null
  closesOn: string | null
  closedMessage: string | null
  introText: string | null
  classes: { id: number; name: string; level: number; series: string | null; placesLeft: number | null }[]
  school: { name: string }
}
